// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useUser } from '@/app/user-context';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import { buildAccessHeaders } from '@/services/claimHttp';

import type { TourPhoto, TourPhotoList, TourPhotoRoundGroup } from '../types';

type UseTourPhotosOptions = {
  tourId: string;
  /** Narrow to one round. Group counts still cover the whole tour, so the chips stay navigable. */
  roundId?: string | null;
  holeNumber?: number | null;
};

type UseTourPhotosResult = {
  photos: TourPhoto[];
  rounds: TourPhotoRoundGroup[];
  unassignedCount: number;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  /** Removes the photo server-side, then drops it from the list. */
  deletePhoto: (photoId: string) => Promise<boolean>;
  /**
   * Promotes the photo to the tour's cover image. Accepted rather than applied: the cover only
   * changes once the resize pipeline has derived it and tour-service has persisted the update.
   */
  setAsTourImage: (photoId: string) => Promise<boolean>;
  /** Flips the photo's public flag server-side, then mirrors it into the list. */
  setPhotoVisibility: (photoId: string, isPublic: boolean) => Promise<boolean>;
};

const EMPTY: TourPhotoList = { photos: [], rounds: [], unassignedCount: 0 };

/**
 * Loads a tour's photo gallery.
 *
 * Plain `fetch` rather than a query library — there is no react-query in this app. State lives here
 * rather than in the screen so the grid, the lightbox and (later) the upload queue all read one
 * list.
 */
export function useTourPhotos({ tourId, roundId, holeNumber }: UseTourPhotosOptions): UseTourPhotosResult {
  const { user } = useUser();
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const [list, setList] = useState<TourPhotoList>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Guards against a slow response from a previous filter overwriting a newer one, and against
  // setting state after the screen has gone.
  const requestSeqRef = useRef(0);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    if (!tourId || !user?.token) {
      setList(EMPTY);
      setLoading(false);
      return;
    }

    const seq = ++requestSeqRef.current;
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (roundId) {
        params.set('roundId', roundId);
      }
      if (typeof holeNumber === 'number') {
        params.set('holeNumber', String(holeNumber));
      }
      const query = params.toString();
      const url = `${apiBaseUrl}/api/tours/${encodeURIComponent(tourId)}/photos${query ? `?${query}` : ''}`;

      const response = await fetch(url, { headers: buildAccessHeaders(user.token) });
      if (!response.ok) {
        throw new Error(`photos request failed with ${response.status}`);
      }

      const payload = (await response.json()) as unknown;
      if (seq !== requestSeqRef.current || !mountedRef.current) {
        return;
      }

      setList(normalizeList(payload));
    } catch (cause) {
      if (seq !== requestSeqRef.current || !mountedRef.current) {
        return;
      }
      setError(cause instanceof Error ? cause.message : 'photos request failed');
      setList(EMPTY);
    } finally {
      if (seq === requestSeqRef.current && mountedRef.current) {
        setLoading(false);
      }
    }
  }, [apiBaseUrl, holeNumber, roundId, tourId, user?.token]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const deletePhoto = useCallback(
    async (photoId: string): Promise<boolean> => {
      if (!tourId || !photoId || !user?.token) {
        return false;
      }

      try {
        const response = await fetch(
          `${apiBaseUrl}/api/tours/${encodeURIComponent(tourId)}/photos/${encodeURIComponent(photoId)}`,
          { method: 'DELETE', headers: buildAccessHeaders(user.token) },
        );
        // 404 counts as success: the row is already gone, and the caller wants the tile removed.
        if (!response.ok && response.status !== 404) {
          throw new Error(`delete failed with ${response.status}`);
        }
      } catch (cause) {
        if (mountedRef.current) {
          setError(cause instanceof Error ? cause.message : 'delete failed');
        }
        return false;
      }

      if (mountedRef.current) {
        setList((previous) => dropPhoto(previous, photoId));
      }
      return true;
    },
    [apiBaseUrl, tourId, user?.token],
  );

  const setAsTourImage = useCallback(
    async (photoId: string): Promise<boolean> => {
      if (!tourId || !photoId || !user?.token) {
        return false;
      }

      try {
        const response = await fetch(
          `${apiBaseUrl}/api/tours/${encodeURIComponent(tourId)}/photos/${encodeURIComponent(photoId)}/cover`,
          { method: 'POST', headers: buildAccessHeaders(user.token) },
        );
        if (!response.ok) {
          throw new Error(`setting the tour image failed with ${response.status}`);
        }
      } catch (cause) {
        if (mountedRef.current) {
          setError(cause instanceof Error ? cause.message : 'setting the tour image failed');
        }
        return false;
      }

      // Nothing local to update: the cover lives on the tour, not on any photo row.
      return true;
    },
    [apiBaseUrl, tourId, user?.token],
  );

  const setPhotoVisibility = useCallback(
    async (photoId: string, isPublic: boolean): Promise<boolean> => {
      if (!tourId || !photoId || !user?.token) {
        return false;
      }

      try {
        const response = await fetch(
          `${apiBaseUrl}/api/tours/${encodeURIComponent(tourId)}/photos/${encodeURIComponent(photoId)}/visibility`,
          {
            method: 'PUT',
            headers: { ...buildAccessHeaders(user.token), 'Content-Type': 'application/json' },
            body: JSON.stringify({ isPublic }),
          },
        );
        if (!response.ok) {
          throw new Error(`visibility update failed with ${response.status}`);
        }
      } catch (cause) {
        if (mountedRef.current) {
          setError(cause instanceof Error ? cause.message : 'visibility update failed');
        }
        return false;
      }

      if (mountedRef.current) {
        setList((previous) => ({
          ...previous,
          photos: previous.photos.map((photo) => (photo.id === photoId ? { ...photo, isPublic } : photo)),
        }));
      }
      return true;
    },
    [apiBaseUrl, tourId, user?.token],
  );

  return {
    photos: list.photos,
    rounds: list.rounds,
    unassignedCount: list.unassignedCount,
    loading,
    error,
    refresh,
    deletePhoto,
    setAsTourImage,
    setPhotoVisibility,
  };
}

/**
 * Removes a photo and keeps the group counts honest, so the round chips do not claim photos that
 * are no longer there until the next refresh.
 */
const dropPhoto = (list: TourPhotoList, photoId: string): TourPhotoList => {
  const removed = list.photos.find((photo) => photo.id === photoId);
  if (!removed) {
    return list;
  }

  return {
    photos: list.photos.filter((photo) => photo.id !== photoId),
    rounds: removed.roundId
      ? list.rounds
          .map((round) =>
            round.roundId === removed.roundId
              ? { ...round, photoCount: Math.max(0, round.photoCount - 1) }
              : round,
          )
          .filter((round) => round.photoCount > 0)
      : list.rounds,
    unassignedCount: removed.roundId ? list.unassignedCount : Math.max(0, list.unassignedCount - 1),
  };
};

// The BFF relays tour-service JSON verbatim and its casing has not been consistent historically
// (see the camel+Pascal fallbacks in useTourEditData), so read both.
const pick = <T,>(source: Record<string, unknown>, ...names: string[]): T | undefined => {
  for (const name of names) {
    const value = source[name];
    if (value !== undefined && value !== null) {
      return value as T;
    }
  }
  return undefined;
};

const normalizeList = (payload: unknown): TourPhotoList => {
  if (!payload || typeof payload !== 'object') {
    return EMPTY;
  }

  const source = payload as Record<string, unknown>;
  const rawPhotos = pick<unknown[]>(source, 'photos', 'Photos') ?? [];
  const rawRounds = pick<unknown[]>(source, 'rounds', 'Rounds') ?? [];

  return {
    photos: rawPhotos
      .filter((entry): entry is Record<string, unknown> => !!entry && typeof entry === 'object')
      .map(normalizePhoto)
      .filter((photo): photo is TourPhoto => photo !== null),
    rounds: rawRounds
      .filter((entry): entry is Record<string, unknown> => !!entry && typeof entry === 'object')
      .map(normalizeRound)
      .filter((round): round is TourPhotoRoundGroup => round !== null),
    unassignedCount: pick<number>(source, 'unassignedCount', 'UnassignedCount') ?? 0,
  };
};

const normalizePhoto = (source: Record<string, unknown>): TourPhoto | null => {
  const id = pick<string>(source, 'id', 'Id');
  if (!id) {
    return null;
  }

  return {
    id,
    tourId: pick<string>(source, 'tourId', 'TourId') ?? '',
    roundId: pick<string>(source, 'roundId', 'RoundId') ?? null,
    holeNumber: pick<number>(source, 'holeNumber', 'HoleNumber') ?? null,
    uploadedByPlayerId: pick<string>(source, 'uploadedByPlayerId', 'UploadedByPlayerId') ?? '',
    uploadedByName: pick<string>(source, 'uploadedByName', 'UploadedByName') ?? null,
    caption: pick<string>(source, 'caption', 'Caption') ?? null,
    contentType: pick<string>(source, 'contentType', 'ContentType') ?? 'image/jpeg',
    width: pick<number>(source, 'width', 'Width') ?? null,
    height: pick<number>(source, 'height', 'Height') ?? null,
    hasThumb: pick<boolean>(source, 'hasThumb', 'HasThumb') ?? false,
    hasMain: pick<boolean>(source, 'hasMain', 'HasMain') ?? false,
    isPublic: pick<boolean>(source, 'isPublic', 'IsPublic') ?? false,
    createdAt: pick<string>(source, 'createdAt', 'CreatedAt') ?? '',
  };
};

const normalizeRound = (source: Record<string, unknown>): TourPhotoRoundGroup | null => {
  const roundId = pick<string>(source, 'roundId', 'RoundId');
  if (!roundId) {
    return null;
  }

  return {
    roundId,
    name: pick<string>(source, 'name', 'Name') ?? null,
    courseName: pick<string>(source, 'courseName', 'CourseName') ?? '',
    playDate: pick<string>(source, 'playDate', 'PlayDate') ?? null,
    photoCount: pick<number>(source, 'photoCount', 'PhotoCount') ?? 0,
  };
};
